import { TextField } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import Cancel from "@material-ui/icons/Cancel";
import InputAdornment from "@material-ui/core/InputAdornment";
import MaskedInput from "react-text-mask";

const styles = (theme) => ({
  fullWidth: { width: "100%" },
  fieldRoot: {},
  label: {
    color: theme.TextColor,
    transform: "translate(14px, 14px) scale(1)",
    fontSize: theme.headerTextFontSize,
    opacity: 0.7,
    "&$disabledLabel": {
      color: theme.iconColor,
    },
  },
  disabledLabel:{},
  notchedOutline: {},
  inputRoot: {
    "& $notchedOutline,$inputMultiline": {
      padding: 0,
    },
    "&$inputDisabled": {
      padding: 0,
      background: theme.tooltipBGColor,
    },
    "& $notchedOutline, &:hover:not($inputFocused):not($inputDisabled):not($inputError) $notchedOutline":
      {
        borderColor: `${theme.iconColor}`,
      },
    "&$inputFocused $notchedOutline": {
      borderWidth: 1,
    },
  },
  inputRootWarning: {
    "& $notchedOutline, &:hover:not($inputFocused):not($inputDisabled):not($inputError) $notchedOutline":
      {
        borderColor: theme.inputWarningLabelColor,
      },

    "& $notchedOutline, &$inputFocused $notchedOutline": {
      borderColor: theme.inputWarningLabelColor,
    },
  },
  inputFocused: {},
  inputDisabled: {
    "& $notchedOutline, &:not($inputFocused):not($inputError) $notchedOutline":
      {
        borderColor: theme.iconColor,
      },
  },
  inputError: {},
  inputMultiline: {},
  inputField: {
    padding: 12,
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    height: "initial",
  },
  numberInputWithoutIncrementer: {
    padding: 12,
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    height: "initial",
    "&[type=number]": {
      "-moz-appearance": "textfield",
    },
    "&::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    "&::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
  },
  iconClass: {
    color: theme.iconColor,
    cursor: "pointer",
    visibility: "visible",
  },
  hiddenIcon: {
    visibility: "hidden",
  },
  adornmentClass: {
    color: theme.iconColor,
  },
  warningLabel: {
    color: theme.TextColor,
    transform: "translate(14px, 14px) scale(1)",
    fontSize: theme.headerTextFontSize,
    opacity: 0.7,
    "&$focusedLabel": {
      color: theme.inputWarningLabelColor,
    },
  },
  focusedLabel: {},
  helperTextRoot:{
     marginTop:0
  },
  helperText: {
    marginBottom: 0,
    color: theme.TextColor,
    opacity:0.7
  },
  error: {
    marginBottom: 0,
  },
});

const CustomTextInput = ({ inputRef, mask, value,...other }) => {
  return (
    <MaskedInput
      {...other}
      ref={(ref) => {
        inputRef(ref ? ref.inputElement : null);
      }}
      mask={mask}
      pipe={(conformedValue) => (conformedValue ? conformedValue.trim() : "")}
      guide
      value={value ? value : ""}
    />
  );
};

const CustomTextField = ({
  classes,
  label,
  value,
  error,
  handleChange,
  showWarning,
  mask,
  type,
  hideIncrementer,
  hideClearIcon,
  isDecimalAllowed,
  inputProps,
  maxLength,
  InputPropsClasses,
  disabled,
  readOnly,
  onBlur,
  helperText,
  startAdornment,
  endAdornment,
  mainClassProps = {},
  labelClassProps = {},
  startAdornmentClasses = {},
  endAdornmentClasses = {},
  ...other
}) => {
  const clearField = () => {
    handleChange("");
  };

  const skipNonNumericInput = (e) => {
    if (isDecimalAllowed && e.key === ".") {
      return;
    }
    ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault();
  };

  const getFieldIcon = () => {
    if (value && !hideClearIcon) {
      return (
        <InputAdornment>
          <Cancel onClick={clearField} classes={{ root: classes.iconClass }} />
        </InputAdornment>
      );
    }
    return null;
  };

  const startAdornmentComponent = startAdornment ? (
    <InputAdornment
      position="start"
      classes={{ root: classes.adornmentClass, ...startAdornmentClasses }}>
      {startAdornment}
    </InputAdornment>
  ) : null;

  const clearAdornmentComponent = disabled ? null : getFieldIcon();

  const endAdornmentComponent = endAdornment ? (
    <InputAdornment
      position="end"
      classes={{ root: classes.adornmentClass, ...endAdornmentClasses }}>
      {endAdornment}
    </InputAdornment>
  ) : (
    clearAdornmentComponent
  );

  const onChange = (event) => {
    let inputValue =
      type === "number" && event.target.value
        ? Number(event.target.value)
        : event.target.value;
    if (
      type === "number" &&
      inputValue &&
      inputProps?.max &&
      inputValue > inputProps?.max
    ) {
      event.preventDefault();
    } else if (inputProps?.pattern) {
      checkPatternValidation(inputValue)
        ? handleChange(inputValue)
        : event.preventDefault();
    } else {
      handleChange(inputValue);
    }
  };

  const checkPatternValidation = (inputValue) => {
    const format = inputProps.pattern;
    return format.test(inputValue) || inputValue === "";
  };

  const inputComponent = mask ? { inputComponent: CustomTextInput } : {};
  return (
      <TextField
        className={classes.fullWidth}
        autoComplete="off"
        classes={{
          root: classes.fieldRoot,
          ...mainClassProps,
        }}
        InputLabelProps={{
          classes: {
            root: showWarning ? classes.warningLabel : classes.label,
            focused: classes.focusedLabel,
            disabled: classes.disabledLabel,
            ...labelClassProps,
          },
        }}
        InputProps={{
          classes: {
            root: showWarning ? classes.inputRootWarning : classes.inputRoot,
            input: hideIncrementer
              ? classes.numberInputWithoutIncrementer
              : classes.inputField,
            notchedOutline: classes.notchedOutline,
            focused: classes.inputFocused,
            inputMultiline: `${classes.inputMultiline} customScroll`,
            disabled: classes.inputDisabled,
            error: classes.inputError,
            ...InputPropsClasses,
          },
          endAdornment: endAdornmentComponent,
          startAdornment: startAdornmentComponent,
          readOnly,
          ...inputComponent,
          inputProps: { mask, maxLength, ...inputProps },
        }}
        value={value}
        onKeyDown={type === "number" ? skipNonNumericInput : undefined} // when input type="number" , characters like '.','e','-' etc are not captured in onChange event.Therefore restrict them when key is pressed.
        onChange={(event) => onChange(event)}
        margin="normal"
        variant="outlined"
        error={Boolean(error)}
        helperText={helperText || error ?
          <>
            {helperText && <p className={classes.helperText}>{helperText}</p>}
            {error && <p className={classes.error}>{error}</p>}
          </> : null
        }
        type={type}
        label={label}
        disabled={disabled}
        onBlur={(event) => onBlur?.(event.target.value)}
        FormHelperTextProps={{classes: {root:classes.helperTextRoot}}}
        {...other}
      />
  );
};

export default withStyles(styles)(CustomTextField);
